import dayjs from 'dayjs';
import { formatCurrency, calculateVAT, getEffectivePrice } from '.';
import { store } from '../redux/store';

export const receiptHtml = ({
  companyName = 'Magna Foodservice Limited',
  customer = {},
  invoiceNumber,
  orderItems,
  kiosk = {},
  totals,
  copy,
}) => {
  const isMerchant = copy === 'merchant';
  const operator = store.getState()?.authSlice?.user?.name;
  const totalQuantity = orderItems?.reduce((sum, item) => sum + Number(item?.quantity || 0), 0);

  const itemRows = orderItems
    ?.filter((item) => item?.product_name || item?.item_name)
    ?.map(
      (item) => {
        const effectivePrice = getEffectivePrice(item);
        const qty = Number(item.quantity || item.order_item_quantity) || 0;
        const { subtotal } = calculateVAT({
          unitPrice: effectivePrice,
          quantity: qty,
          vatField: item.vat,
        });

        return `
        <tr class="item-detail-row" >
          <td style="width: 47%;" class="item-name-cell">
            ${item.product_name || item.item_name}${item.vat > 0 ? '<strong>*</strong>' : ''}
          </td>
          <td style="width: 10%;" class="center bold">${item.uom}</td>
          <td style="width: 10%;" class="center bold">${qty}</td>
          <td style="width: 18%;" class="center bold">${formatCurrency(effectivePrice)}</td>
          <td style="width: 25%;" class="left bold">${formatCurrency(subtotal)}</td>
        </tr>
      `;
      }
    )
    .join('');

  // Matches a real card receipt's layout (e.g. Costco): left-aligned "Label value"
  // lines, not a right-aligned amounts table - and only whichever fields actually
  // exist, since Worldpay (terminal) has real card-present detail while Sticky
  // (hosted, card-not-present) only ever has a reference and a paid-at time.
  const paymentDetail = totals?.paymentDetail;
  const line = (label, value) =>
    value || value === 0
      ? `<div><span><b>${label}:</b></span><span style="padding-right: 5px;"> ${value}</span></div>`
      : '';
  const paymentLines = [
    totals?.cash ? line('Cash', formatCurrency(totals.cash)) : '',
    totals?.card ? line('Card', formatCurrency(totals.card)) : '',
    totals?.online_payment ? line('Online', formatCurrency(totals.online_payment)) : '',
    totals?.on_account_payment ? line('On Account', formatCurrency(totals.on_account_payment)) : '',
    totals?.attendant_name ? line('Authorized by', totals.attendant_name) : '',
    paymentDetail?.maskedNumber ? line('Card Number', paymentDetail.maskedNumber) : '',
    paymentDetail?.brand ? line('Card Type', paymentDetail.brand) : '',
    paymentDetail?.entryMode ? line('Card Entry', paymentDetail.entryMode) : '',
    paymentDetail?.authCode ? line('Approval Code', paymentDetail.authCode) : '',
    paymentDetail?.approvedAt || paymentDetail?.paidAt
      ? line(
        'Date/Time',
        dayjs(paymentDetail.approvedAt || paymentDetail.paidAt).format('DD/MM/YYYY HH:mm:ss')
      )
      : '',
    paymentDetail?.reference ? line('Reference #', paymentDetail.reference) : '',
    paymentDetail?.sessionId ? line('Session', paymentDetail.sessionId) : '',
    paymentDetail ? line('Status', 'APPROVED') : '',
    paymentDetail ? line('Finalized', dayjs().format('DD/MM/YYYY HH:mm:ss')) : '',
  ].join('');

  const invoiceHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>Invoice ${invoiceNumber || 'INV-0001'}</title>
        <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;700&display=swap" rel="stylesheet">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          @page {
            size: 80mm auto;
            margin: 0;
          }
          
          @media print {
            body {
              width: 80mm;
            }
          }
          
          body {
          font-family: 'IBM Plex Mono', monospace;
            width: 80mm;
            max-width: 80mm;
            margin: 0 auto;
            padding: 3mm;
            font-size: 14px;
            line-height: 1.4;
            color: #000;
            background: #fff;
            -webkit-font-smoothing: antialiased;
            letter-spacing: -0.3px;  /* tighter character spacing */
            word-spacing: -1px; 
          }          
          
          .invoice-header {
            text-align: center;
            margin-bottom: 10px;
          }
          
          .logo {
            max-width: 200px;
            height: auto;
          }
          .margin-left-img {
            margin-left: 24px;
          }
          
          .company-name {
            font-size: 16px;
            margin-bottom: 4px;
          }
          
          .invoice-number {
            font-size: 11px;
            margin-bottom: 8px;
            margin-top: 5px !important;
            font-weight: bold;
          }
          
          .divider {
            border-bottom: 1px dashed #333;
            margin: 8px 0;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 4px;
            tabel-layout: fixed;
          }

          .header-row td:not(:first-child) {
            border-left: 1px dashed #333;
          }

          .header-row td {
            border-bottom: 1px dashed #333;
          }

          .item-detail-row td:not(:first-child) {
            border-left: 1px dashed #333;
          }
          
          .header-row td {
            font-weight: bold;
            padding-bottom: 4px;
            font-size: 11px;
            padding-left: 2px;
            padding-right: 2px;
          }
          
          .item-name-cell {
            padding: 6px 0 2px 0;
            word-wrap: break-word;
            word-break: break-word;
          }
          
          .item-detail-row td {
            padding: 0 2px 6px 2px;
            font-size: 11px;
          }
          
          .totals-table {
            margin-top: 8px;
          }
          
          .totals-table td {
            padding: 2px 0;
          }
          
          .totals-table .label {
            text-align: left;
            padding-right: 8px;
          }
          
          .grand-total {
            font-size: 14px;
            font-weight: bold;
            padding-top: 4px !important;
          }
          
          .right {
            text-align: right;
          }
            .right-margin {
            margin-right: 5px;
          }
            .left {
            text-align: left;
          }
          .center {
            text-align: center;
          }
          
          .bold {
            font-weight: bold;
          }
          
          .footer {
            text-align: center;
            margin-top: 12px;
            font-size: 11px;
            line-height: 1.6;
          }
          
          .footer-divider {
            margin: 10px 0 8px 0;
          }
        </style>
      </head>
      <body>
        <div class="invoice-header">
          ${copy ? `<div class="company-name" style="margin-bottom: 8px;"><b>${isMerchant ? 'MERCHANT COPY' : 'CUSTOMER COPY'}</b></div>` : ''}
          <img src="https://d35jitumpuemmk.cloudfront.net/assets/magna-logo-thermal-printer.png" alt="Logo" class="logo" />
          <div class="company-name">${companyName}</div>
          <div class="company-address" style="font-size: 12px; margin-bottom: 3px; line-height: 0.5; white-space: pre-line; text-align: center;">
          Unit 19-21 Phoenix Distribution Park
          <br />
          Phoenix Way, Heston,  TW5 9NB
          <br />
          TEL: 0208 050 1231
          <br />
          VAT Number: 186346479
          <br />
          www.magnafoodservice.co.uk
          <br />
        </div>
          <div class="invoice-number">
            Customer: ${customer?.code || ''} - ${customer?.title || ''}
          </div>
          <div class="invoice-number">Invoice No. : ${invoiceNumber}</div>
          ${kiosk?.terminalId
      ? `<div class="invoice-number">Kiosk: ${kiosk.terminalId}${kiosk?.storeId ? ` (${kiosk.storeId})` : ''}</div>`
      : ''
    }
        </div>
        
        <div class="divider"></div>
        
        <table>
          <thead>
            <tr class="header-row">
              <td style="width: 47%;">Item</td>
              <td class="center" style="width: 10%;">UOM</td>
              <td class="center" style="width: 10%;">Qty</td>
              <td class="center" style="width: 18%;">Price</td>
              <td class="left" style="width: 25%;">Total</td>
            </tr>
          </thead>
          <tbody>
            ${itemRows}
          </tbody>
        </table>
        
        <div class="divider"></div>
        
        <table class="totals-table">
          <tbody>
            <tr>
              <td class="label">Subtotal:</td>
              <td class="right bold">${formatCurrency(totals.subtotal)}</td>
            </tr>
            <tr>
              <td class="label">VAT:</td>
              <td class="right bold">${formatCurrency(totals.vat)}</td>
            </tr>
            <tr class="grand-total">
              <td class="label">Grand Total:</td>
              <td class="right bold">${formatCurrency(totals.grandTotal)}</td>
            </tr>
            ${totals?.remaining_amount !== undefined && totals?.remaining_amount !== 0
      ? `
            <tr>
              <td class="label bold">Balance:</td>
              <td class="right bold">${formatCurrency(totals.remaining_amount)}</td>
            </tr>
            `
      : ''
    }
          </tbody>
        </table>

        ${paymentLines
      ? `
        <div class="divider footer-divider"></div>
        ${paymentLines}
        `
      : ''
    }

        <div class="divider footer-divider"></div>

        <div>
        <span><b>Total Items Quantity:</b></span>
          <span style="padding-right: 5px;">${totalQuantity}</span>
        </div>
          <div>
        <span><b>Total Items:</b></span>
        <span style={{ paddingRight: 5 }}>
${orderItems?.filter((item) => item?.product_name || item?.item_name)?.length || 0}
</span> 

        </div>
          <div>
        <span><b>Operator:</b></span>
          <span style="padding-right: 5px;">${operator || ''}</span>
        </div>
        <div style="display: flex; justify-content: space-between;">
        <div>
        <span><b>Date:</b></span>
          <span style="padding-right: 10px;">${dayjs().format('DD-MM-YYYY')}</span>
        </div>
          <div>
        <span><b>Time:</b></span>
          <span style="padding-right: 10px;">${dayjs().format('HH:mm:ss')}</span>
        </div>
        </div>
        <div class="divider footer-divider"></div>

        <div class="footer">
          ${copy === 'customer' ? '<b>Customer copy - please keep for your records.</b><br/>' : ''}${isMerchant ? '<b>Merchant copy - keep with the till records.</b><br/>' : ''}Thank you for your purchase.<br/>
          By purchasing, you agree to our Terms:<br/>
          magnafoodservice.co.uk/terms-conditions/
        </div>
        <br/>
        <img src="https://d35jitumpuemmk.cloudfront.net/assets/powered-by-prefectos-logo.png" alt="Logo" class="logo margin-left-img" />
        <div style="height: 20px;"></div>

        
      
      </body>
    </html>
  `;

  return invoiceHTML;
};

// The kiosk app drops a receipt popup that has not asked to print within ~3s of opening.
const LOAD_FALLBACK_MS = 2000;

const printHtml = (html, extraDelayMs = 0) =>
  new Promise((resolve) => {
    const printWindow = window.open('', '_blank', 'width=350,height=600');
    if (!printWindow) {
      alert('Please allow popups to print invoices');
      resolve(false);
      return;
    }

    let started = false;
    const print = () => {
      if (started) return;
      started = true;
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        resolve(true);
      }, 300 + extraDelayMs);
    };

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    // With nothing left to load (cached images) the load event has already fired or never
    // will, so waiting on it alone can leave the receipt unprinted.
    if (printWindow.document.readyState === 'complete') {
      print();
    } else {
      printWindow.onload = print;
      setTimeout(print, LOAD_FALLBACK_MS);
    }
  });

export const printReceipt = (details) => printHtml(receiptHtml(details));

const bodyOf = (html) => html.slice(html.indexOf('<body>') + 6, html.lastIndexOf('</body>'));

// One document, a page break between the copies, for a browser: it allows a single popup per
// click, so two print jobs would be blocked.
const SEPARATOR = '<div style="break-before: page; page-break-before: always;"></div>';

export const bothCopiesHtml = (details) => {
  const customer = receiptHtml({ ...details, copy: 'customer' });
  const merchant = receiptHtml({ ...details, copy: 'merchant' });
  return customer.replace('</body>', () => `${SEPARATOR}${bodyOf(merchant)}</body>`);
};

// The kiosk app ends the order shortly after the first receipt has printed and replaces the page,
// so a slip that is only started after that is lost. Both are opened at once and asked to print
// a moment apart: the printer queues them in that order, each as its own job with its own cut.
const COPY_STAGGER_MS = 400;
// Set by the kiosk app's own preload; a normal browser never has it.
const inKioskApp = () => typeof window.__mfKioskPrint === 'function';

export const printReceiptCopies = async (details) => {
  if (!inKioskApp()) return printHtml(bothCopiesHtml(details));

  const results = await Promise.all([
    printHtml(receiptHtml({ ...details, copy: 'customer' })),
    printHtml(receiptHtml({ ...details, copy: 'merchant' }), COPY_STAGGER_MS),
  ]);
  return results.every(Boolean);
};
